import { ArrowRightIcon } from '@heroicons/react/24/outline';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ContentAudience } from '../utils/audience';
import type { ResourceEntry, ResourceEntryKind } from '../utils/resourceIndex';
import { RESOURCE_KIND_LABEL } from '../utils/resourceIndex';
import InternalBadge from './InternalBadge';
import ResourceKindIcon from './ResourceKindIcon';

export interface Hub {
    kind: ResourceEntryKind;
    title: string;
    to: string;
    count: number;
    /** The newest items, shown as direct links into the section. */
    links: ResourceEntry[];
}

/** Link rows surfaced per "Start here" tile. */
export const HUB_LINK_COUNT = 3;

/** The gray icon square used by tiles without a kind icon. */
export const HubIconSquare: React.FC<{ children: ReactNode }> = ({ children }) => (
    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex-shrink-0">
        {children}
    </span>
);

/** Card frame shared by the "Start here" tiles: header row, then the tile's body. */
export const HubCardFrame: React.FC<{
    icon: ReactNode;
    title: ReactNode;
    count?: number;
    children?: ReactNode;
}> = ({ icon, title, count, children }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col gap-4 hover:border-gray-300 hover:shadow transition-all">
        <div className="flex items-center gap-3">
            {icon}
            {title}
            {count !== undefined && (
                <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
                    {count} {count === 1 ? 'item' : 'items'}
                </span>
            )}
        </div>
        {children}
    </div>
);

/** Chip link row inside a "Start here" tile: resting tint that lifts on hover. */
export const HubLinkRow: React.FC<{
    to: string;
    title: string;
    rightLabel?: string;
    audience?: ContentAudience;
}> = ({ to, title, rightLabel, audience }) => (
    <Link
        to={to}
        className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 border border-transparent hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all group"
    >
        <span className="text-sm font-semibold text-gray-700 truncate group-hover:text-gray-900">
            {title}
        </span>
        <InternalBadge audience={audience} />
        {rightLabel && (
            <span className="ml-auto flex-shrink-0 text-xs text-gray-400">{rightLabel}</span>
        )}
    </Link>
);

/** A "Start here" section tile: its count, newest links, and Browse all. */
export const HubCard: React.FC<{ hub: Hub }> = ({ hub }) => (
    <HubCardFrame
        icon={<ResourceKindIcon kind={hub.kind} className="w-8 h-8" iconClassName="w-4 h-4" />}
        title={
            <Link to={hub.to} className="text-base font-bold text-gray-800 hover:underline">
                {hub.title}
            </Link>
        }
        count={hub.count}
    >
        {hub.links.length > 0 && (
            <div className="flex flex-col gap-1.5 border-t border-gray-200 pt-3">
                {hub.links.map((entry) => (
                    <HubLinkRow
                        key={entry.key}
                        to={entry.path}
                        title={entry.title}
                        rightLabel={RESOURCE_KIND_LABEL[entry.kind]}
                        audience={entry.audience}
                    />
                ))}
            </div>
        )}

        <Link
            to={hub.to}
            className="mt-auto self-end inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:gap-2.5 transition-all"
        >
            Browse all
            <ArrowRightIcon className="w-3.5 h-3.5" />
        </Link>
    </HubCardFrame>
);

export default HubCard;
